import React, {useEffect, useState, useCallback} from 'react'
//components
import { Button } from '@components/ui/buttons/Button'
import EditableTable from '@components/ui/editableTable/EditableTable'
import { ValidationErrors } from '@components/ui/messages/ValidationErrors'
//table config
import { RunControlSchema } from '../tableConfig/jobControlSchemas'
import { RunColumnNameMap } from '../tableConfig/jobControlColumnNameMaps'
//hooks
import useConfirmDialog from '@hooks/useConfirmDialog'
import { useExportExcel } from "@hooks/useExportExcel"
import { useLoading } from '@contexts/LoadingContext'
import { useError } from '@contexts/ErrorContext'
import { useUnsavedChanges } from '@contexts/UnsavedChangesContext'
import { useSuccess } from '@contexts/SuccessContext'
//api, util functions
import { handleAddRow } from '../helperFunctions/handleAddRow'
import { getRunsAPI, createRunAPI, updateRunAPI, deleteRunAPI } from "@services/runsAPI"
import { areObjectsEqual } from "@utils/areObjectsEqual"
import { resetStateAfterDelete } from "../helperFunctions/resetStateAfterDelete"
import { validateTableData } from '../helperFunctions/validateTableData';
import { useUser } from '@contexts/UserContext'

const initialData = [{
    "Run_Name": "",
    "Low_Speed": "",
    "High_Speed": "",
    "IsActive": "",
}]

/**
 * Run Control - create, update, delete, and activate runs
 * 
 * @param {DropdownOption} selectedJob
 * 
 * @returns {JSX.Element}
 * 
 * @category Components - Job Control
 */
export const RunControl = ({selectedJob}) => {
    //set state
    const [data, setData] = useState(initialData)
    const [originalData, setOriginalData] = useState([]) //use to compare original and changed data when time to submit

    const [validationErrors, setValidationErrors] = useState([])
  
    const [ConfirmDialog, requestConfirm] = useConfirmDialog()
    const {handleExportToExcel} = useExportExcel()
    const { showLoading, hideLoading } = useLoading()
    const {showError} = useError()
    const {setHasUnsavedChanges} = useUnsavedChanges()
    const { timeoutSuccess } = useSuccess()
    const { setStoredJob } = useUser()
    
    //fetch runs function
    const fetchRuns = async(jobId) => {
        showLoading("Getting runs...")

        let response = await getRunsAPI(jobId);

        if(!response.success){
            hideLoading()
            showError(`Error getting runs: ${response.message}`);
        } else {
            
            if(response.data.length > 0){
                const formattedData = response.data.map(({IsStuck, HasLaunched, ...run}) => {
                    return {
                        ...run,
                        IsActive: run.IsActive === true ? "Active" : "Inactive"
                    }
                });

                setData(formattedData);
                setOriginalData(formattedData);
                hideLoading();
                return;
            }

            //if no data was returned from the fetch, reset state to initial data
            setData(initialData);
            hideLoading()
        }

    }

    //fetch runs when component loads or if job selected/changed
    useEffect(() => {
        if(!selectedJob) return 

        fetchRuns(selectedJob.value)
        setValidationErrors([])

    }, [selectedJob]) //eslint-disable-line

    //helper functions

    const handleSubmit = async() =>{
        //set loading message, remove old submit error message if exists
        showLoading("Submitting...")
        
        //validate
        const isValid = await validateTableData(data, setValidationErrors, RunControlSchema)
        
        if(!isValid){
            hideLoading();
            return;
        } 

        //create copy of data that can be updated with new RunIds
        const updatedData = [...data]; 

        for (let i = 0; i < updatedData.length; i++) {
            const run = updatedData[i]

            //UPDATE RUNS - runs that are not new (have a RunID) and are different from the original data (to reduce api calls)
            if(run.hasOwnProperty("RunID")){

                //compare to original data to see if run data has changed
                //if it has, update the data
                const originalRow = originalData.find(original => original.RunID === run.RunID)

                if(!areObjectsEqual(run, originalRow)){
                    const updatedRun = {
                        ...run,
                        JobID: selectedJob.value,
                        IsActive: run.IsActive === "Active" ? true : false,
                    }

                    if(updatedRun.HasLaunched) delete updatedRun.HasLaunched;
                    
                    const response = await updateRunAPI(updatedRun)

                    //if not successful, update error message and return
                    if(!response.success){
                        hideLoading()

                        showError(`Error updating runs: ${response.message}`)
                        
                        return
                    }
                }

            } else {

                //CREATE RUNS - if no RunID, it's a new run, so create it
                const newRun = {
                    ...run,
                    JobID: selectedJob.value,
                    IsActive: run.IsActive === "Active" ? true : false
                }

                const response = await createRunAPI(newRun);

                //if not successful, update error message and return
                if(!response.success){
                    hideLoading()

                    showError(`${response.message}`)

                    return
                }

                //once created, add RunID to the run
                updatedData[i] = {
                    ...run,
                    RunID: response.data.RunID
                };
                
           }
           
        }
        
        //reset state 
        setData(updatedData)
        setOriginalData(updatedData)
        setValidationErrors([])

        setStoredJob(prev => {
            return {
                ...prev,
                RunCount: updatedData.length
            }
        })

        //let user know that data submitted successfully

        hideLoading()

        setHasUnsavedChanges(false)

        timeoutSuccess("Saved")
    }

    const handleDeleteOneRun = useCallback(async(rowData, rowIndex) => {
        showLoading("Deleting run...")

        const RunID = rowData.RunID

        //if run has id, it exists in database, so need to delete it from there
        if(RunID){

            const response = await deleteRunAPI(RunID)

            if(!response.success){
                hideLoading()

                showError(`Error deleting run: ${response.message}`)

                return
            }
        }

        //remove run from state
        resetStateAfterDelete(setData, rowIndex, initialData)

        setStoredJob(prev => {
            return {
                ...prev,
                RunCount: data.length - 1
            }
        })

        hideLoading()
        
        timeoutSuccess("Delete successful")
        
    }, [showLoading, deleteRunAPI, hideLoading, resetStateAfterDelete, initialData, setData]) //eslint-disable-line


    const handleDeleteAllRuns = async() => {
        //confirm user wants to delete all runs
        const isConfirmed = await requestConfirm('Are you sure you want to delete all runs?', 'Delete Runs', 'Cancel');
        if(!isConfirmed) return

        // user confirmed
        showLoading("Deleting runs...")

        //if run has id, it exists in database, so need to delete it from there

        for(const run of data){
            if(run.RunID){
                const response = await deleteRunAPI(run.RunID)

                if(!response.success){
                    hideLoading()

                    showError(`Error deleting runs: ${response.message}`)
                        
                    return
                } 
            }
        }
            
        //reset table state 
        setData(initialData);
            
        setStoredJob(prev => {
            return {
                ...prev,
                RunCount: 0
            }
        })

        hideLoading()
            
        setHasUnsavedChanges(false)

        timeoutSuccess("Delete successful")

    }


    return (
        <div className='martrak-max-width'>

            {selectedJob ? 
            (
                <>
                    {/* excel export button and error message*/}
                    <div className='flex justify-end'>
                        <Button 
                            variant='noBackground' 
                            onClick={() => handleExportToExcel(data, RunColumnNameMap, "runs", selectedJob.label)}
                            className="text-sm"
                        >
                            <i className="fa-solid fa-file-export text-purple-800 mr-2"></i>
                            Export
                        </Button>
                    </div>

                    {/* table */}
                    <EditableTable 
                        data={data} 
                        setData={setData} 
                        schema={RunControlSchema} 
                        canDeleteRow={true} 
                        handleDeleteRow={handleDeleteOneRun}
                        validationErrors={validationErrors}
                    />

                    {/* buttons */}
                    <div className='flex justify-end mb-5'>
                        <Button variant='success' onClick={() => handleAddRow(RunControlSchema, setData)} className='w-max'><i className="fa-solid fa-circle-plus mr-2"></i>New run</Button>
                    </div>

                    <div className='flex'>
                        <Button variant='success' onClick={handleSubmit} className='w-max mr-2'>Submit</Button>
                        <Button variant="danger" onClick={handleDeleteAllRuns} className='w-max'><i className="fa-solid fa-trash-can mr-2"></i>Delete All Runs</Button>
                    </div>
                    
                    {/* validation error messages */}
                    <ValidationErrors validationErrors={validationErrors} columnNameMap={RunColumnNameMap}/>

                </>
            ) 
            : 
            (
                <div className='flex justify-center mt-10'>
                    <p className='text-purple-600 font-bold italic'>Please select a job.</p>
                </div>
            )
            }
            
            {/* dialog box opens when "Delete All Runs" button is clicked */}
            <ConfirmDialog />

        </div>
    )
}